import axios, { AxiosError } from 'axios';
import router from '@/router';
import store from '@/store';
import { LelyAuth } from '@hn-work/frontend-vue-shared';

declare module 'axios' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface AxiosRequestConfig<D = any> {
        // Error statuses the caller handles itself, e.g. [404] when a missing resource is expected
        silentErrorStatuses?: number[];
    }
}

const reportedErrors = new WeakSet<object>();

// Lets callers with their own error handling skip a second alert for an error already shown to the user
const isReportedHttpError = (error: unknown): boolean => typeof error === 'object'
    && error !== null
    && reportedErrors.has(error);

const errorMessage = (error: AxiosError<any>): string => {
    const request = `${(error.config.method || 'get').toUpperCase()} ${error.config.url}`;

    // Network and CORS failures reach axios with status 0 (axios sets the raw XHR as the response)
    if (!error.response?.status) {
        return `No readable response from the server (${request}). It may be unreachable or have returned an error.`;
    }

    const { status, data } = error.response;
    const serverMessage = typeof data?.message === 'string' ? ` ${data.message}` : '';

    return `Something went wrong (HTTP ${status}, ${request}).${serverMessage}`;
};

const shouldReport = (error: AxiosError): boolean => {
    if (axios.isCancel(error)) {
        return false;
    }

    // Unauthenticated requests (e.g. a failed login) are handled by the login view
    if (!LelyAuth.isAuthenticated()) {
        return false;
    }

    const status = error.response?.status;

    if (!status) {
        return true;
    }

    return status >= 400
        && status !== 401
        && !(error.config.silentErrorStatuses || []).includes(status);
};

axios.interceptors.response.use((response) => response, (error: any) => {
    if (error.response?.status === 401 && !router.currentRoute.meta?.guest) {
        LelyAuth.logout();

        router.push({ name: 'login' }).then();
    }

    if (shouldReport(error)) {
        reportedErrors.add(error);
        store.commit('addError', errorMessage(error));
    }

    return Promise.reject(error);
});

export default isReportedHttpError;
